import React, { useMemo, useState, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp, Search, Download } from 'lucide-react';
import { DocumentStats } from '../utils/dynamodb';

interface DocumentStatsTableProps {
  stats: DocumentStats[];
  isLoading: boolean;
  onExportCSV: () => void;
  RefreshButton?: React.ReactNode;
}

const columnHelper = createColumnHelper<DocumentStats>();

export const DocumentStatsTable: React.FC<DocumentStatsTableProps> = ({
  stats,
  isLoading,
  onExportCSV,
  RefreshButton,
}) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());
  const [agentSourceFilter, setAgentSourceFilter] = useState('');
  const [minCountFilter, setMinCountFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const toggleCellExpansion = useCallback((cellId: string) => {
    setExpandedCells((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cellId)) {
        newSet.delete(cellId);
      } else {
        newSet.add(cellId);
      }
      return newSet;
    });
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('document_uri', {
        header: 'Document URI',
        cell: (info) => {
          const uri = info.getValue();
          const cellId = `uri-${info.row.index}`;
          const isExpanded = expandedCells.has(cellId);
          const shouldTruncate = uri.length > 60;
          const filename = uri.split('/').pop() || uri;

          return (
            <div className="text-sm text-gray-900">
              <div
                className={`${shouldTruncate ? 'cursor-pointer hover:bg-gray-50 p-1 rounded' : ''}`}
                onClick={() => shouldTruncate && toggleCellExpansion(cellId)}
                title={uri}
              >
                {shouldTruncate && !isExpanded
                  ? `${uri.substring(0, 60)}...`
                  : uri}
              </div>
              {filename !== uri && (
                <div className="text-xs text-gray-500 mt-1">
                  File: {filename}
                </div>
              )}
            </div>
          );
        },
        size: 300,
        minSize: 200,
        maxSize: 500,
      }),
      columnHelper.accessor('title', {
        header: 'Title',
        cell: (info) => {
          const title = info.getValue() || 'N/A';
          const cellId = `title-${info.row.index}`;
          const isExpanded = expandedCells.has(cellId);
          const shouldTruncate = title.length > 40;

          return (
            <div className="text-sm text-gray-700">
              <div
                className={`${shouldTruncate ? 'cursor-pointer hover:bg-gray-50 p-1 rounded' : ''}`}
                onClick={() => shouldTruncate && toggleCellExpansion(cellId)}
                title={title}
              >
                {shouldTruncate && !isExpanded
                  ? `${title.substring(0, 40)}...`
                  : title}
              </div>
            </div>
          );
        },
        size: 200,
        minSize: 150,
        maxSize: 300,
      }),
      columnHelper.accessor('agent_source', {
        header: 'Agent Source',
        cell: (info) => (
          <div className="text-sm text-gray-600">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {info.getValue() || 'Unknown'}
            </span>
          </div>
        ),
        size: 120,
        minSize: 100,
        maxSize: 180,
      }),
      columnHelper.accessor('count', {
        header: 'Citation Count',
        cell: (info) => (
          <div className="text-sm font-semibold text-gray-900">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              {info.getValue()}
            </span>
          </div>
        ),
        size: 100,
        minSize: 80,
        maxSize: 150,
      }),
      columnHelper.accessor('last_updated', {
        header: 'Last Updated',
        cell: (info) => {
          const timestamp = info.getValue();
          const date = new Date(timestamp);
          const formatted =
            date.toLocaleDateString() + ' ' + date.toLocaleTimeString();

          return (
            <div className="text-sm text-gray-500">
              <div title={timestamp}>{formatted}</div>
            </div>
          );
        },
        size: 180,
        minSize: 140,
        maxSize: 220,
      }),
    ],
    [expandedCells, toggleCellExpansion],
  );

  const filteredData = useMemo(() => {
    return stats.filter((stat) => {
      const matchesGlobal =
        globalFilter === '' ||
        stat.document_uri.toLowerCase().includes(globalFilter.toLowerCase()) ||
        (stat.title || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (stat.agent_source || '')
          .toLowerCase()
          .includes(globalFilter.toLowerCase());

      const matchesAgentSource =
        agentSourceFilter === '' ||
        (stat.agent_source || '')
          .toLowerCase()
          .includes(agentSourceFilter.toLowerCase());

      const matchesMinCount =
        minCountFilter === '' ||
        stat.count >= parseInt(minCountFilter) ||
        isNaN(parseInt(minCountFilter));

      return matchesGlobal && matchesAgentSource && matchesMinCount;
    });
  }, [stats, globalFilter, agentSourceFilter, minCountFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: {
        pageSize: 20,
      },
      sorting: [
        {
          id: 'count',
          desc: true,
        },
      ],
    },
  });

  const clearFilters = () => {
    setGlobalFilter('');
    setAgentSourceFilter('');
    setMinCountFilter('');
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg flex flex-col h-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Document Citation Statistics
          </h3>
        </div>
        <div className="flex-1 p-6">
          <div className="animate-pulse">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded w-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Document Citation Statistics ({filteredData.length})
          </h3>
          <div className="flex items-center gap-3">
            {RefreshButton}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <Search className="h-4 w-4 mr-2" />
              Search
            </button>
            <button
              onClick={onExportCSV}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Documents & Titles
              </label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search URIs and titles..."
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agent Source
              </label>
              <input
                type="text"
                placeholder="Filter by agent source"
                value={agentSourceFilter}
                onChange={(e) => setAgentSourceFilter(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Citation Count
              </label>
              <input
                type="number"
                placeholder="Min citations"
                value={minCountFilter}
                onChange={(e) => setMinCountFilter(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={clearFilters}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}

      {/* Pagination (moved above table) */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing{' '}
            {table.getState().pagination.pageIndex *
              table.getState().pagination.pageSize +
              1}{' '}
            to{' '}
            {Math.min(
              (table.getState().pagination.pageIndex + 1) *
                table.getState().pagination.pageSize,
              table.getFilteredRowModel().rows.length,
            )}{' '}
            of {table.getFilteredRowModel().rows.length} results
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-hidden">
        <div className="overflow-auto h-full">
          <table
            className="w-full divide-y divide-gray-200"
            style={{ width: table.getCenterTotalSize() }}
          >
            <thead className="bg-gray-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="sticky top-0 z-30 bg-gray-50"
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 relative"
                      style={{
                        width: header.getSize(),
                      }}
                    >
                      <div
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getIsSorted() && (
                          <span>
                            {header.column.getIsSorted() === 'desc' ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronUp className="h-4 w-4" />
                            )}
                          </span>
                        )}
                      </div>
                      {header.column.getCanResize() && (
                        <div
                          className="absolute right-0 top-0 h-full w-1 bg-gray-300 cursor-col-resize hover:bg-blue-500 opacity-0 hover:opacity-100"
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-6 py-4 text-sm"
                      style={{
                        width: cell.column.getSize(),
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
